import { Injectable } from '@angular/core';
import { FeasibilityService } from '../../../../services/feasibility/feasibility.service';
import { BuyerApiService } from '../../../../../shared/services/buyer-api/survey.service';
import {
  FeasibilityPayload,
  FeasibilityRequest,
  FeasibilityResponse,
} from '../../../../../shared/interfaces/feasibility.interface';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import {
  BuyerSurveyResponse,
  SurveyNoTargetingResponse,
  UpdateSurveyStatus,
} from '../../../../../shared/interfaces/survey.interface';
import { SURVEY_STATUS } from '../../../../../shared/constants/survey-status';
import {
  McSpvSurveyNoTargetingResponse,
  McSpvSurveyRequest,
  McSurveyRequest,
  MultiCountrySurvey,
} from '../interface/multi-country.interface';
import { notifyMessage } from '../../../../../shared/constants/notify-message';

@Injectable({
  providedIn: 'root',
})
export class MultiCountryService {
  constructor(
    private _feasibilityService: FeasibilityService,
    private _buyerApiService: BuyerApiService,
    private _toaster: ToasterService
  ) {}

  public addSurvey(
    payload: McSurveyRequest
  ): Observable<
    | { error: unknown; payload: McSurveyRequest }
    | { response: SurveyNoTargetingResponse; payload: McSurveyRequest }
  > {
    return this._buyerApiService.addSurvey(payload).pipe(
      map((response) => ({
        response,
        payload,
      })),
      catchError((error) =>
        of({
          error,
          payload,
        })
      )
    );
  }

  public updateSurvey(payload: McSpvSurveyRequest, surveyId: number) {
    return this._buyerApiService.updateMcQbSurvey(payload, surveyId);
  }

  public deleteMcSurveys(mcSurveys: MultiCountrySurvey[]) {
    return forkJoin(
      mcSurveys.map((mcSurvey) =>
        this._buyerApiService.deleteDraftSurvey(mcSurvey.survey_id)
      )
    ).pipe(
      catchError(() => {
        this._toaster.error(notifyMessage.errorMessage.DRAFT_SURVEY_DELETE);
        return of({});
      }),
      tap(() => {
        this._toaster.success(
          notifyMessage.successMessage.DRAFT_SURVEYS_DELETED
        );
      })
    );
  }

  public buildFeasibilityPayloadForPreDraft(
    surveyRequest: McSurveyRequest
  ): FeasibilityPayload {
    return this._feasibilityService.buildFeasibilityPayload(
      [],
      surveyRequest,
      undefined,
      []
    );
  }

  public getFeasibility(
    feasibilityRequest: FeasibilityRequest
  ): Observable<FeasibilityResponse | null> {
    return this._feasibilityService.getFeasibility(feasibilityRequest).pipe(
      catchError((err) => {
        this._toaster.error(err.error.msg);
        return of(null);
      })
    );
  }

  public getSurveys(
    surveyData: BuyerSurveyResponse
  ): Observable<BuyerSurveyResponse[]> {
    const surveyStatus = Object.values(SURVEY_STATUS).filter(
      (status) => typeof status === 'number'
    );
    return this._buyerApiService
      .getSurveys(surveyStatus.join(','), 1, '', surveyData.mc_unique_id, 1)
      .pipe(
        catchError((err) => {
          this._toaster.error(err.error.msg);
          return of([]);
        })
      );
  }

  public updateSurveyStatus(
    survey: MultiCountrySurvey,
    status: number
  ): Observable<{
    payload: MultiCountrySurvey;
    response: UpdateSurveyStatus;
  } | null> {
    return this._buyerApiService
      .updateSurveyStatus(survey.survey_id, {
        ps_survey_status: status,
      })
      .pipe(
        map((response) => {
          return {
            payload: survey,
            response,
          };
        }),
        catchError((err) => {
          this._toaster.error(
            notifyMessage.errorMessage.SURVEY_LAUNCH.LIVE_URL_ERR_FROM_API ==
              err.error.ps_api_response_message
              ? `${notifyMessage.errorMessage.SURVEY_LAUNCH.EMPTY_LIVE_URL} to ${survey.country_name}-${survey.language_name} Survey`
              : `Survey ${survey.country_name}-${survey.language_name} - ${err.error.ps_api_response_message}`
          );
          return of(null);
        })
      );
  }

  public addMCSpvSurvey(
    payload: McSpvSurveyRequest
  ): Observable<McSpvSurveyNoTargetingResponse | null> {
    return this._buyerApiService.addMCSpvSurvey(payload).pipe(
      catchError((err) => {
        this._toaster.error(err.error.ps_api_response_message);
        return of(null);
      })
    );
  }
}
